// ====================================================================
// ziven-core — 共享模块: 「ziven-core 自己的后台 iframe 入口」判定 +
// 给 <html> 打 marker class。
//
// ── 这个模块解决什么 ───────────────────────────────────────────────
// 论坛里点后台入口走的是 forum 侧 adminPanelModal.js 的 iframe
// (`/admin?_iframe=ziven-core-r15`)。iframe 里的后台跟顶层直访的 `/admin`
// 需求不同, 所以有一批「只在 iframe 内生效」的后台定制 (去掉仪表盘
// 「公告」「扩展状态」两块, 去掉左上角返回控件)。
//
// ⚠️ 之前, 这个 marker 字符串 `'ziven-core-r15'` 在**两个地方各写死
//    一份**:
//      · js/src/forum/modal/adminPanelModal.js  (iframe 的 src)
//      · js/src/admin/utils/hideDashboardWidgetsInFrame.js  (判定用的常量)
//    改一处不改另一处 ⇒ **静默失效** (不报错、不白屏, 只是定制不生效)。
//    把两份合并到本模块, 一处定义, 两边 import。
//
// ── 影响范围 (红线) ─────────────────────────────────────────────────
// **只在 ziven-core iframe 入口内生效**, 直访 `/admin` (顶层窗口) 完全不受影响。
// 判定条件 (**两个都要满足**, 缺一不成立):
//   (1) window.self !== window.top —— 当前文档确实在 iframe 内
//   (2) location.search 里的 `_iframe` 参数 === 'ziven-core-r15'
//       —— 确实来自 ziven-core 那个入口
// ⇒ 别人把 `/admin` 塞进别的 iframe 也不受影响 (条件 2 不成立)。
//
// ── 本模块被谁用 ───────────────────────────────────────────────────
//   · js/src/forum/modal/adminPanelModal.js  → import IFRAME_MARKER (拼 src)
//   · js/src/admin/utils/hideDashboardWidgetsInFrame.js → import 判定函数
//   · js/src/admin/utils/hideBackControlInFrame.js      → import 打标记函数
// forum bundle 与 admin bundle 都能 import 本模块 (js/src/common/ 下, 两个
// entry 各自带一份; 没人 import 的绑定会被 tree-shaking 摇掉, 无副作用)。
// ====================================================================

/**
 * ziven-core 后台 iframe 入口的 URL 标记。
 *
 * forum 侧拼 iframe src 用它, admin 侧判定「我是不是在这个 iframe 里」用它。
 * ⚠️ 改这个值 = 同时改了两处语义 (iframe src + 判定), 必须一起改。
 */
export const IFRAME_MARKER = 'ziven-core-r15';

/**
 * 判定当前文档是否处于「ziven-core 自己的 iframe 入口」内。
 *
 * 每次调用都重新算 (不缓存) —— 顶层窗口和 iframe 窗口是两份独立的 JS 上下文,
 * 万一将来条件变了, 每次算才不会留脏状态。
 *
 * @returns {boolean} 判定不成立 / 判定过程出错时, 保守返回 false。
 */
export function isZivenCoreAdminFrame() {
  try {
    // (1) 必须在 iframe 里。跨域时读 window.top 的属性会抛 SecurityError,
    //     但这里只做**引用比较**, 不读属性 ⇒ 跨域也安全, 不会抛。
    if (window.self === window.top) {
      return false;
    }

    // (2) 必须来自 ziven-core 那个入口 (双条件)。
    const params = new URLSearchParams(window.location.search);
    return params.get('_iframe') === IFRAME_MARKER;
  } catch (e) {
    // 判定本身出错 ⇒ 保守起见当作「不是」, 不做任何定制。
    return false;
  }
}

/**
 * 判定成立时, 给 <html> 挂上 marker class `ziven-core-admin-frame`。
 *
 * 有了这个 class, less/admin.less 里就能用 CSS 精准地只对 iframe 内的
 * 后台做定制 (顶层直访 `/admin` 没有这个 class ⇒ 一根毛都不影响)。
 *
 * 幂等: `classList.add` 对已存在的 class 是 no-op, 可重复调。
 *
 * @returns {boolean} 是否真的打上了标记。
 */
export function markZivenCoreAdminFrame() {
  try {
    if (!isZivenCoreAdminFrame()) {
      return false;
    }

    // documentElement = <html>。挂在 <html> 上 ⇒ 整个文档(含 iframe 里
    // 后台自己生成的 DOM) 都能被 `.ziven-core-admin-frame .xxx` 命中。
    document.documentElement.classList.add('ziven-core-admin-frame');
    return true;
  } catch (e) {
    // 打标记失败 (极端情况) ⇒ 保守返回 false, 不做任何定制。
    return false;
  }
}
export default markZivenCoreAdminFrame;
flarum.reg.add('ziiven-ziven-core', 'common/helpers/zivenCoreAdminFrame', markZivenCoreAdminFrame);